import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Sparkles, Download, Save } from "lucide-react";
import AdminLayout from "../../components/AdminLayout";
import { api } from "../../lib/api";

export default function AdminMockup() {
  const [designFile, setDesignFile] = useState(null);
  const [designPreview, setDesignPreview] = useState("");
  const [color, setColor] = useState("black");
  const [extra, setExtra] = useState("");
  const [busy, setBusy] = useState(false);
  const [mockup, setMockup] = useState("");
  const fileRef = useRef();

  const onFile = (f) => {
    if (!f) return;
    setDesignFile(f);
    const reader = new FileReader();
    reader.onload = () => setDesignPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

  const generate = async () => {
    if (!designFile) { toast.error("Upload a design first"); return; }
    setBusy(true);
    setMockup("");
    try {
      const b64 = await fileToBase64(designFile);
      const res = await api.post("/admin/mockup", {
        design_base64: b64,
        tshirt_color: color,
        prompt_extra: extra,
      }, { timeout: 120000 });
      setMockup(res.data.image);
      toast.success("MOCKUP GENERATED");
    } catch (err) {
      toast.error("GENERATION FAILED", { description: err?.response?.data?.detail?.slice(0, 100) || "Try again" });
    } finally {
      setBusy(false);
    }
  };

  const saveToCatalog = async () => {
    if (!mockup) return;
    const slug = window.prompt("Product slug (unique, lowercase):", `mockup-${Date.now()}`);
    if (!slug) return;
    const name = window.prompt("Product name:", "Cult Lyf Custom Tee");
    if (!name) return;
    try {
      await api.post("/products", {
        name,
        slug,
        description: "Custom mockup design — heavyweight cotton tee.",
        price: 49,
        images: [mockup],
        sizes: ["S","M","L","XL"],
        colors: [color === "black" ? "Black" : "White"],
        collection: "drop-01",
        stock: 50,
        featured: false,
        mockup_design: designPreview,
      });
      toast.success("SAVED TO CATALOG");
    } catch (err) {
      toast.error("SAVE FAILED", { description: err?.response?.data?.detail || "" });
    }
  };

  return (
    <AdminLayout title="AI Mockup Generator">
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Left: Controls */}
        <div className="bg-[#0A0A0A] border border-[#27272A] p-6">
          <p className="label-mono text-[#CFF048]">// INPUT // STEP 01</p>
          <h2 className="font-display font-black text-2xl uppercase mt-2">Upload Design</h2>

          <div
            className="dropzone mt-6 p-10 text-center cursor-pointer"
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("drag"); }}
            onDragLeave={(e) => e.currentTarget.classList.remove("drag")}
            onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove("drag"); onFile(e.dataTransfer.files[0]); }}
            data-testid="mockup-dropzone"
          >
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e.target.files[0])} data-testid="mockup-file-input" />
            {designPreview ? (
              <img src={designPreview} alt="design" className="max-h-48 mx-auto" />
            ) : (
              <>
                <Upload size={40} className="text-[#CFF048] mx-auto" />
                <p className="font-display font-bold mt-3">DROP YOUR DESIGN HERE</p>
                <p className="label-mono text-[#71717A] mt-1">PNG / JPG with transparent BG preferred</p>
              </>
            )}
          </div>

          <p className="label-mono text-[#CFF048] mt-8">// STEP 02 // T-SHIRT COLOR</p>
          <div className="flex gap-2 mt-3">
            {["black", "white"].map((c) => (
              <button key={c} onClick={() => setColor(c)}
                className={`flex-1 px-4 py-3 border label-mono uppercase ${color === c ? "border-[#CFF048] text-[#CFF048]" : "border-[#27272A]"}`}
                data-testid={`color-${c}`}
              >{c}</button>
            ))}
          </div>

          <p className="label-mono text-[#CFF048] mt-6">// STEP 03 // EXTRA PROMPT (OPTIONAL)</p>
          <textarea
            value={extra}
            onChange={(e) => setExtra(e.target.value)}
            placeholder="e.g. oversized fit, dark studio backdrop, model holding the tee..."
            rows={3}
            className="cult-input mt-3"
            data-testid="mockup-extra-prompt"
          />

          <button onClick={generate} disabled={busy || !designFile} className="btn-neon w-full mt-6" data-testid="generate-mockup-btn">
            <Sparkles size={18} /> {busy ? "GENERATING..." : "GENERATE MOCKUP"}
          </button>
        </div>

        {/* Right: Output */}
        <div className="bg-[#0A0A0A] border border-[#27272A] p-6 relative">
          <p className="label-mono text-[#CFF048]">// OUTPUT // CULTLYF-GEN-01</p>
          <h2 className="font-display font-black text-2xl uppercase mt-2">Result</h2>

          <div className="mt-6 bg-black border border-[#27272A] aspect-square flex items-center justify-center overflow-hidden" data-testid="mockup-output">
            {busy ? (
              <div className="text-center">
                <p className="term-loader text-base">GENERATING_MOCKUP</p>
                <p className="label-mono text-[#71717A] mt-3">{`>`}_ STITCHING_PIXELS<br/>{`>`}_ APPLYING_FABRIC_TEXTURE<br/>{`>`}_ RENDERING_LIGHT</p>
              </div>
            ) : mockup ? (
              <img src={mockup} alt="generated mockup" className="w-full h-full object-contain" />
            ) : (
              <p className="label-mono text-[#71717A]">// AWAITING_INPUT</p>
            )}
          </div>

          {mockup && (
            <div className="mt-4 flex gap-3">
              <a href={mockup} download="cultlyf-mockup.png" className="btn-outline flex-1" data-testid="download-mockup-btn">
                <Download size={16} /> DOWNLOAD
              </a>
              <button onClick={saveToCatalog} className="btn-neon flex-1" data-testid="save-to-catalog-btn">
                <Save size={16} /> SAVE TO CATALOG
              </button>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
